import React from 'react';
import { StyleSheet, css } from 'aphrodite';
import { TextInputComponent } from './TextInputComponent';
import { CommonStyles } from '../styles/Styles';
import Tooltip from '@material-ui/core/Tooltip';
import Icon from '@material-ui/core/Icon';

class ROI extends React.Component {
  constructor(props) {
    super(props);
    
    this.state = {
      polygons: [], // Stored in canvas coordinates with stable IDs
      originalPolygons: [], // Stored in original image coordinates for persistence with stable IDs
      currentPolygon: [],
      isDrawing: false,
      enableAnnotation: false,
      dragInfo: null,
      imageWidth: 0,  // Original image width
      imageHeight: 0, // Original image height
      canvasWidth: 0, // Current canvas width
      canvasHeight: 0, // Current canvas height
      image: null,
      selectedPolygonId: null, // Changed from index to ID
      selectedPolygonName: "",
      roiName: {}, // Now maps stable IDs to names
      roiStateTitle: "Start",
      isInitialized: false, // Track if we've loaded initial ROIs
      nextRoiId: 0 // Counter for generating unique ROI IDs
    };
    
    this.canvasRef = React.createRef();
    this.containerRef = React.createRef();
  }

  setROIName = (val) => {
    let roiMap = { ...this.state.roiName };
    roiMap[this.state.selectedPolygonId] = val;
    this.setState({
      roiName: roiMap,
      selectedPolygonName: val // Update the selected name for immediate display
    }, () => {
      console.log("ROI name updated:", this.state.selectedPolygonId, val);
    });
  }

  // Convert canvas coordinates to original image coordinates
  canvasToOriginal = (canvasCoords) => {
    const { imageWidth, imageHeight, canvasWidth, canvasHeight } = this.state;
    if (!imageWidth || !imageHeight || !canvasWidth || !canvasHeight) return canvasCoords;
    
    return canvasCoords.map(point => ({
      x: Math.round((point.x / canvasWidth) * imageWidth),
      y: Math.round((point.y / canvasHeight) * imageHeight),
    }));
  }

  // Convert original image coordinates to canvas coordinates
  originalToCanvas = (originalCoords) => {
    const { imageWidth, imageHeight, canvasWidth, canvasHeight } = this.state;
    if (!imageWidth || !imageHeight || !canvasWidth || !canvasHeight) return originalCoords;
    
    return originalCoords.map(point => ({
      x: Math.round((point.x / imageWidth) * canvasWidth),
      y: Math.round((point.y / imageHeight) * canvasHeight),
    }));
  }

  handleSave = () => {
    const { originalPolygons, roiName } = this.state;

    const convertedPolygons = originalPolygons.map((polygonData) => ({
      roiName: roiName[polygonData.id] || `ROI ${polygonData.id}`,
      roiId: polygonData.id,
      polygon: polygonData.polygon,
      originalWidth: this.state.imageWidth,
      originalHeight: this.state.imageHeight,
    }));

    console.log("Save to DB:", convertedPolygons);
    this.props.saveROI(convertedPolygons);
  };

  componentDidMount() {
    const img = new Image();
    img.src = this.props.image;
    img.onload = () => {
      this.setState({ 
        image: img,
        imageWidth: img.naturalWidth,
        imageHeight: img.naturalHeight
      }, () => {
        this.resizeCanvas();
        window.addEventListener('resize', this.handleResize);
      });
    };
    window.addEventListener('keydown', this.handleKeyDown);
  }
  
  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('keydown', this.handleKeyDown);
  }
  
  handleResize = () => {
    this.resizeCanvas();
  };
  
  resizeCanvas = () => {
    if (!this.containerRef.current || !this.canvasRef.current) return;
    
    const container = this.containerRef.current;
    const canvas = this.canvasRef.current;
    
    // Get container width, respecting any max-width constraints
    const containerWidth = container.clientWidth;
    
    // Calculate responsive height maintaining aspect ratio
    const { imageWidth, imageHeight } = this.state;
    const aspectRatio = imageHeight / imageWidth;
    const canvasWidth = containerWidth;
    const canvasHeight = containerWidth * aspectRatio;
    
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    // Update state with new canvas dimensions
    this.setState({ 
      canvasWidth, 
      canvasHeight 
    }, () => {
      // After canvas dimensions are set, handle ROI initialization or conversion
      this.handleCanvasResize();
    });
  };

  handleCanvasResize = () => {
    const { isInitialized, originalPolygons } = this.state;
    
    if (!isInitialized) {
      // First time initialization - load ROIs from props
      this.initializeROIs();
    } else {
      // Canvas resize - convert existing originalPolygons to new canvas coordinates
      const polygons = originalPolygons.map(polygonData => ({
        id: polygonData.id,
        polygon: this.originalToCanvas(polygonData.polygon)
      }));
      this.setState({ polygons }, () => this.drawCanvas());
    }
  }

  initializeROIs = () => {
    let polygons = [];
    let originalPolygons = [];
    let roiMap = {};
    let maxId = -1;
    
    if (this.props.cam && this.props.cam.rois && this.props.cam.rois.length > 0) {
      this.props.cam.rois.forEach(element => {
        const roiId = element.roiId !== undefined ? element.roiId : element.roiName || maxId + 1;
        maxId = Math.max(maxId, roiId);
        
        roiMap[roiId] = element.roiName;
        // Store original coordinates with stable ID
        originalPolygons.push({
          id: roiId,
          polygon: element.polygon
        });
        // Convert to canvas coordinates with stable ID
        polygons.push({
          id: roiId,
          polygon: this.originalToCanvas(element.polygon)
        });
      });
    }

    this.setState({
      polygons,
      originalPolygons,
      roiName: roiMap,
      nextRoiId: maxId + 1,
      isInitialized: true
    }, () => this.drawCanvas());
  }
  
  handleKeyDown = (e) => {
    // Only handle delete key if not typing in a text input
    if (e.key === 'Delete' && e.target.tagName !== 'INPUT') {
      this.deleteROI();
    }
  }
  
  deleteROI = () => {
    const { selectedPolygonId, polygons, originalPolygons, roiName } = this.state;
    if (selectedPolygonId !== null) {
      const updatedPolygons = polygons.filter(p => p.id !== selectedPolygonId);
      const updatedOriginalPolygons = originalPolygons.filter(p => p.id !== selectedPolygonId);
      
      // Remove from roiName mapping
      const updatedRoiName = { ...roiName };
      delete updatedRoiName[selectedPolygonId];
      
      this.setState({ 
        polygons: updatedPolygons,
        originalPolygons: updatedOriginalPolygons,
        roiName: updatedRoiName,
        selectedPolygonId: null, 
        selectedPolygonName: "" 
      }, () => {
        this.handleSave();
        this.drawCanvas();
      });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // Redraw canvas when relevant state changes
    const { image, polygons, currentPolygon, dragInfo, canvasWidth, canvasHeight } = this.state;
    
    if (image && 
        (prevState.polygons !== polygons || 
         prevState.currentPolygon !== currentPolygon || 
         prevState.dragInfo !== dragInfo)) {
      this.drawCanvas();
    }
  }
  
  drawCanvas = () => {
    const canvas = this.canvasRef.current;
    const { image, polygons, currentPolygon, canvasWidth, canvasHeight } = this.state;
    
    if (!canvas || !image || !canvasWidth || !canvasHeight) return;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    
    // Draw image
    ctx.drawImage(image, 0, 0, canvasWidth, canvasHeight);
    
    // Draw completed polygons
    polygons.forEach((polygonData) => {
      const points = polygonData.polygon;
      const polygonId = polygonData.id;
      
      // Draw filled polygon
      if (points.length >= 3) {
        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < points.length; i++) {
          ctx.lineTo(points[i].x, points[i].y);
        }
        ctx.closePath();
        
        // Highlight selected polygon
        if (polygonId === this.state.selectedPolygonId) {
          ctx.fillStyle = 'rgba(255, 255, 0, 0.3)';
        } else {
          ctx.fillStyle = 'rgba(255, 0, 0, 0.2)';
        }
        
        ctx.fill();
        ctx.strokeStyle = polygonId === this.state.selectedPolygonId ? 'yellow' : 'red';
        ctx.lineWidth = 2;
        ctx.stroke();
      }
      
      // Draw vertices
      points.forEach((point) => {
        ctx.beginPath();
        ctx.arc(point.x, point.y, 6, 0, 2 * Math.PI);
        ctx.fillStyle = 'rgba(255, 0, 0, 0.7)';
        ctx.fill();
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 2;
        ctx.stroke();
      });
    });
    
    // Draw current polygon being drawn
    if (currentPolygon.length > 0) {
      // Draw lines
      ctx.beginPath();
      ctx.moveTo(currentPolygon[0].x, currentPolygon[0].y);
      for (let i = 1; i < currentPolygon.length; i++) {
        ctx.lineTo(currentPolygon[i].x, currentPolygon[i].y);
      }
      ctx.strokeStyle = 'blue';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.stroke();
      ctx.setLineDash([]);
      
      // Draw vertices
      currentPolygon.forEach((point) => {
        ctx.beginPath();
        ctx.arc(point.x, point.y, 5, 0, 2 * Math.PI);
        ctx.fillStyle = 'blue';
        ctx.fill();
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 1;
        ctx.stroke();
      });
    }
  };
  
  getMousePos = (canvas, evt) => {
    const rect = canvas.getBoundingClientRect();
    return {
      x: evt.clientX - rect.left,
      y: evt.clientY - rect.top
    };
  };
  
  findPointUnderMouse = (mousePos) => {
    const { polygons, currentPolygon } = this.state;
    const hitRadius = 10; // Detection radius in pixels
    
    // Check existing polygons first
    for (let i = 0; i < polygons.length; i++) {
      const polygonData = polygons[i];
      for (let pointIndex = 0; pointIndex < polygonData.polygon.length; pointIndex++) {
        const point = polygonData.polygon[pointIndex];
        const distance = Math.sqrt(
          Math.pow(point.x - mousePos.x, 2) + 
          Math.pow(point.y - mousePos.y, 2)
        );
        
        if (distance <= hitRadius) {
          return { 
            type: 'existing', 
            polygonId: polygonData.id,
            pointIndex 
          };
        }
      }
    }
    
    // Check current polygon points
    for (let i = 0; i < currentPolygon.length; i++) {
      const point = currentPolygon[i];
      const distance = Math.sqrt(
        Math.pow(point.x - mousePos.x, 2) + 
        Math.pow(point.y - mousePos.y, 2)
      );
      
      if (distance <= hitRadius) {
        return { 
          type: 'current', 
          pointIndex: i 
        };
      }
    }
    
    return null;
  };

  isPointInPolygon = (point, polygon) => {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x, yi = polygon[i].y;
      const xj = polygon[j].x, yj = polygon[j].y;
  
      const intersect = ((yi > point.y) !== (yj > point.y)) &&
        (point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  };
  
  handleMouseDown = (e) => {
    // Skip handling if the click is inside an input field
    if (e.target.tagName === 'INPUT') {
      return;
    }
    
    const canvas = this.canvasRef.current;
    const mousePos = this.getMousePos(canvas, e);
    const { polygons, roiName } = this.state;
  
    // First check if user clicked on a polygon
    let foundPolygon = false;
    for (let i = 0; i < polygons.length; i++) {
      if (this.isPointInPolygon(mousePos, polygons[i].polygon)) {
        const polygonId = polygons[i].id;
        const selectedName = roiName[polygonId] || "";
        
        this.setState({ 
          selectedPolygonId: polygonId,
          selectedPolygonName: selectedName
        }, () => {
          console.log("Selected polygon:", polygonId, "Name:", selectedName);
          this.drawCanvas();
        });
        foundPolygon = true;
        break;
      }
    }
    
    if (foundPolygon) {
      return;
    }
    
    // Otherwise handle drawing/dragging points
    const { isDrawing, currentPolygon } = this.state;
    const pointInfo = this.findPointUnderMouse(mousePos);
    
    if (pointInfo) {
      // Start dragging an existing point
      this.setState({ dragInfo: pointInfo });
    } else if (!isDrawing) {
      if (!this.state.enableAnnotation) return;

      // Start a new polygon
      this.setState({ 
        isDrawing: true,
        currentPolygon: [mousePos],
        selectedPolygonId: null,
        selectedPolygonName: ""
      });
    } else {
      if (!this.state.enableAnnotation) return;

      // Add a point to the current polygon
      this.setState({
        currentPolygon: [...currentPolygon, mousePos]
      });
    }
  };
  
  handleMouseMove = (e) => {
    // Skip handling if mouse is over an input field
    if (e.target.tagName === 'INPUT') {
      return;
    }
    
    const { dragInfo, polygons, currentPolygon, originalPolygons } = this.state;
    if (!dragInfo) return;
    
    const canvas = this.canvasRef.current;
    const mousePos = this.getMousePos(canvas, e);
    
    if (dragInfo.type === 'existing') {
      // Update point in an existing polygon
      const newPolygons = [...polygons];
      const newOriginalPolygons = [...originalPolygons];
      
      // Find the polygon with the matching ID
      const polygonIndex = newPolygons.findIndex(p => p.id === dragInfo.polygonId);
      if (polygonIndex !== -1) {
        newPolygons[polygonIndex].polygon[dragInfo.pointIndex] = mousePos;
        
        // Update the corresponding original polygon
        newOriginalPolygons[polygonIndex].polygon = this.canvasToOriginal(newPolygons[polygonIndex].polygon);
        
        this.setState({ 
          polygons: newPolygons,
          originalPolygons: newOriginalPolygons
        });
      }
    } else if (dragInfo.type === 'current') {
      // Update point in the current polygon
      const newCurrentPolygon = [...currentPolygon];
      newCurrentPolygon[dragInfo.pointIndex] = mousePos;
      this.setState({ currentPolygon: newCurrentPolygon });
    }
  };
  
  handleMouseUp = () => {
    this.setState({ dragInfo: null });
  };
  
  handleDoubleClick = () => {
    const { currentPolygon, isDrawing, polygons, originalPolygons, nextRoiId } = this.state;
    
    if (currentPolygon.length >= 3 && isDrawing) {
      const newRoiId = nextRoiId;
      const newPolygonData = {
        id: newRoiId,
        polygon: [...currentPolygon]
      };
      const newOriginalPolygonData = {
        id: newRoiId,
        polygon: this.canvasToOriginal(currentPolygon)
      };
      
      this.setState({
        polygons: [...polygons, newPolygonData],
        originalPolygons: [...originalPolygons, newOriginalPolygonData],
        currentPolygon: [],
        enableAnnotation: false,
        isDrawing: false,
        roiStateTitle: "Start",
        nextRoiId: nextRoiId + 1
      });
    }
  };
  
  handleStart = () => {
    this.setState({
      enableAnnotation: true,
      roiStateTitle: "In Progress"
    });
  }

  handleReset = () => {
    this.setState({
      polygons: [],
      originalPolygons: [],
      currentPolygon: [],
      isDrawing: false,
      enableAnnotation: false,
      dragInfo: null,
      selectedPolygonId: null,
      selectedPolygonName: "",
      roiStateTitle: "Start",
      roiName: {},
      nextRoiId: 0
    }, () => {
      this.handleSave();
      this.drawCanvas();
    });
  };

  // Direct input change handler for the ROI name input
  handleRoiNameChange = (e) => {
    const newName = e.target.value;
    this.setROIName(newName);
  };

  render() {
    const { polygons, selectedPolygonId, selectedPolygonName, roiName } = this.state;

    return (
      <div className={css(Styles.container)}>
        <div className={css(Styles.card)}>
        <div className={css(Styles.toolbar)}>
            { (
              <div className={css(Styles.inputContainer)}>
                <label className={css(Styles.inputLabel)}>Enter ROI Name:</label>
                <input
                  disabled = {selectedPolygonId == null}
                  type="text"
                  className={css(Styles.textInput)}
                  value={selectedPolygonName}
                  onChange={this.handleRoiNameChange}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}

            <Tooltip title={'Draw ROI'}>
              <div className={css(CommonStyles.headerMemberOuterMobileStyle)}>
                {
                  <div
                  disabled={this.state.roiStateTitle !== "Start"}
                  className={css(CommonStyles.headerMemberAddContainer)} onClick={this.handleStart}>
                    <Icon className={css(Styles.advancedSettingsIcon)}>edit</Icon>
                  </div>
                }
              </div>
            </Tooltip>
            <Tooltip title={'Reset'}>
              <div className={css(CommonStyles.headerMemberOuterMobileStyle)}>
                {
                  <div
                  className={css(CommonStyles.headerMemberAddContainer)} onClick={this.handleReset}>
                    <Icon className={css(Styles.advancedSettingsIcon)}>refresh</Icon>
                    {/* Reset */}
                  </div>
                }
              </div>
            </Tooltip>
            <Tooltip title={'Save'}>
              <div className={css(CommonStyles.headerMemberOuterMobileStyle)}>
                {
                  <div
                  className={css(CommonStyles.headerMemberAddContainer)}   onClick={this.handleSave}>
                    <Icon className={css(Styles.advancedSettingsIcon)}>save</Icon>
                    {/* Save */}
                  </div>
                }
              </div>
            </Tooltip>
            <Tooltip title={'Delete ROI'}>
              <div className={css(CommonStyles.headerMemberOuterMobileStyle)}>
                {
                  <div
                  className={css(CommonStyles.headerMemberAddContainer)}   onClick={this.deleteROI}>
                    <Icon className={css(Styles.advancedSettingsIcon)}>delete</Icon>
                    {/* Delete */}
                  </div>
                }
              </div>
            </Tooltip>

            <div className={css(Styles.instructions)}>
              <span className={css(Styles.highlight)}>Instructions:</span> Click to start drawing a polygon.
              Continue clicking to add points. Double-click to complete the polygon.
              Drag any red point to adjust polygon shape.
            </div>
          </div>
          <div
            ref={this.containerRef}
            className={css(Styles.canvasWrapper)}
          >
            <canvas
              ref={this.canvasRef}
              onMouseDown={this.handleMouseDown}
              onMouseMove={this.handleMouseMove}
              onMouseUp={this.handleMouseUp}
              onMouseLeave={this.handleMouseUp}
              onDoubleClick={this.handleDoubleClick}
              className={css(Styles.canvas)}
            />
          </div>

        </div>
      </div>
    );
  }
}

const Styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    height: '100%'
  },
  card: {
    marginBottom: '1rem',
    backgroundColor: '#ffffff',
    padding: '1rem',
    borderRadius: '6px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    width: '100%',
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    marginBottom: '0.5rem',
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '1rem',
    marginBottom: '1rem',
    alignItems: 'center',
  },
  button: {
    backgroundColor: '#3b82f6',
    color: 'white',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    ':hover': {
      backgroundColor: '#2563eb',
    },
    ':disabled': {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed',
    }
  },
  instructions: {
    color: '#4b5563',
    flexGrow: 1,
    fontSize: '0.875rem',
  },
  highlight: {
    fontWeight: 600,
  },
  canvasWrapper: {
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    width: '100%',
  },
  canvas: {
    backgroundColor: '#f3f4f6',
    cursor: 'crosshair',
    touchAction: 'none',
    display: 'block',
    width: '100%',
    height: 'auto',
  },
  annotations: {
    marginTop: '1rem',
  },
  annotationsTitle: {
    fontWeight: 600,
  },
  annotationsText: {
    fontSize: '0.875rem',
    color: '#4b5563',
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginRight: '1rem',
  },
  inputLabel: {
    fontSize: '0.875rem',
    fontWeight: '500',
    marginBottom: '0.25rem',
  },
  textInput: {
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    padding: '0.5rem',
    fontSize: '0.875rem',
  },
});

export default ROI;